import type { Resource } from './resources.ts'

/**
 * Laplace x GTM articles: finding buyers, their emails, and sending safely.
 * Numbers come from our own pilot runs (named as such) or from Google's
 * published sender rules; nothing here is a market-wide statistic.
 */
export const GTM_EN_RESOURCES: ReadonlyArray<Resource> = [
  {
    language: 'en',
    type: 'guide',
    slug: 'find-decision-maker-email',
    title: 'How to Find a Decision Maker’s Email Without Buying a Database',
    description:
      'A four-step method that finds verified work emails from public sources: the company’s own site, its address pattern, a verification check and, only for the rest, a paid finder.',
    author: 'Laplace Team',
    publishedAt: '2026-10-04',
    readingTime: '7 min read',
    coverImage: '/images/find-decision-maker-email-news.png',
    coverImageAlt:
      'A company team page flows through email patterns into a verified address',
    topics: ['Lead generation', 'Email finding', 'Outbound'],
    // Unpublished until the payment provider's review: a step-by-step guide to
    // guessing and verifying addresses reads as harvesting, and it shows our
    // method. Soften before bringing it back.
    published: false,
    seoTitle: 'Find a Decision Maker’s Email Without a Database | Laplace',
    seoDescription:
      'Find verified work emails of decision makers from public sources: team pages, the company’s email pattern, SMTP verification and a finder only for the rest.',
    relatedResources: [
      'safe-outreach-email-from-gmail',
      'who-to-sell-to-first',
    ],
    ctaType: 'find-customers',
    keyTakeaways: [
      'Most small companies name their leaders on their own site, and many publish at least one address that reveals their email pattern.',
      'A guessed address is worth nothing until it is verified: an unverified list is how sender reputations get burned.',
      'In our pilot, guessing plus verification proved 76% of addresses; adding a paid finder only for the rest raised that to 95%.',
    ],
    sections: [
      {
        id: 'why-not-buy-a-list',
        title: 'Why not just buy a list',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Contact databases are the fast answer, and they are fine for large companies that everyone sells to. For small and mid-sized businesses they are thinner and older: people change roles, small teams rarely update profiles, and a record copied months ago may point to someone who left.',
          },
          {
            type: 'paragraph',
            text: 'There is also a cost mismatch. You pay for thousands of records to use a few dozen. The method below works the other way round: start from the companies you actually want, find their people, and pay only for the addresses you cannot prove yourself.',
          },
        ],
      },
      {
        id: 'step-1-people',
        title: 'Step 1: Find the people on the company’s own site',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Team, about and leadership pages name founders and department heads, usually with titles and often with links to their LinkedIn profiles. The company site is the most current public source about who works there, because the company maintains it.',
          },
          {
            type: 'list',
            items: [
              'Read the home page links, the sitemap and pages such as /team, /about, /leadership and /company.',
              'Keep the title as written: it tells you which team the person leads, which matters more than seniority alone.',
              'Keep the page each person came from, so every contact can be traced to a source.',
            ],
          },
          {
            type: 'callout',
            title: 'When the site names no one',
            body: 'Many small sites have no team page. Then a people search by company and job title (for example “VP Sales” at the company) fills the gap: in our runs, sites named their leaders for only part of the companies we looked at.',
          },
        ],
      },
      {
        id: 'step-2-pattern',
        title: 'Step 2: Learn the company’s email pattern',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Most companies use one scheme for everyone. If the site publishes even one personal address, such as jane@ on a contact page, you know the pattern for the whole team. Without one, start from the schemes that are most common for small US companies.',
          },
          {
            type: 'table',
            columns: ['Pattern', 'Verified on (of 41 domains)'],
            rows: [
              ['{first}@company.com', '28'],
              ['{first}.{last}@company.com', '5'],
              ['{f}{last}@company.com', '4'],
              ['{first}{l}@company.com', '2'],
            ],
          },
          {
            type: 'paragraph',
            text: 'The table is from our pilot of 62 decision makers at US small businesses. It is a starting order for guesses, not a rule: the first address you prove at a company fixes the pattern for everyone else there.',
          },
        ],
      },
      {
        id: 'step-3-verify',
        title: 'Step 3: Verify every guess before you use it',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A verification service asks the company’s mail server whether it would accept a message for the address, then hangs up before sending anything. The answer is one of four: the mailbox exists, it does not, the domain accepts any address (catch-all), or the server will not say.',
          },
          {
            type: 'list',
            items: [
              'Check guesses most likely first and stop at the first one that exists.',
              'Remember the guesses that failed, so you never pay to check them again.',
              'On a catch-all domain, stop guessing: verification cannot tell a real mailbox from a made-up one there.',
            ],
          },
        ],
      },
      {
        id: 'step-4-finder',
        title: 'Step 4: Use a paid finder only for the rest',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Email finders take a name and a company domain (or a LinkedIn profile) and return an address they have verified. They are the right tool for catch-all domains and unusual schemes, and most charge only when they find something, so using them last keeps the cost low.',
          },
          {
            type: 'metrics',
            items: [
              {
                value: '76%',
                label: 'Guess + verification',
                detail: 'addresses proven without a finder',
              },
              {
                value: '95%',
                label: '+ one finder',
                detail: 'for the people the guesses missed',
              },
              {
                value: '97%',
                label: '+ a second finder',
                detail: 'diminishing returns',
              },
            ],
            sourceNote:
              'Laplace pilot: 62 decision makers at US small businesses, October 2026.',
          },
        ],
      },
      {
        id: 'faq',
        title: 'FAQ',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'Is it legal to email people found this way?',
                answer:
                  'In the US, first-contact B2B email is allowed under CAN-SPAM when you identify yourself, use honest subject lines, include a postal address and an opt-out, and honor opt-outs. Other countries have stricter rules; check them before you send there.',
              },
              {
                question:
                  'Why not send to unverified guesses and see what bounces?',
                answer:
                  'Because bounces are what mailbox providers use to judge you. A high bounce rate sends your next emails, including the ones to real people, to spam.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'en',
    type: 'guide',
    slug: 'safe-outreach-email-from-gmail',
    title: 'How Many Emails per Day Is Safe From One Mailbox?',
    description:
      'Google’s sending limits and sender rules, and the daily pace that keeps a mailbox in good standing when it writes to new business contacts.',
    author: 'Laplace Team',
    publishedAt: '2026-10-04',
    readingTime: '5 min read',
    coverImage: '/images/safe-outreach-email-from-gmail-news.png',
    coverImageAlt:
      'An inbox with a reply, a daily sending pace that ramps up, and a shield',
    topics: ['Deliverability', 'Email', 'Google Workspace'],
    featured: true,
    published: true,
    seoTitle: 'Safe Daily Email Limits per Mailbox | Laplace',
    seoDescription:
      'How many emails one Gmail or Google Workspace mailbox can safely send per day to new business contacts, and the sender rules that decide whether you land in the inbox.',
    relatedResources: ['who-to-sell-to-first'],
    ctaType: 'find-customers',
    keyTakeaways: [
      'Google’s hard sending limit is far above what is safe when you write to people who do not know you yet: the limit that matters is your reputation.',
      'Google asks every sender to authenticate their domain and keep reported spam low; large senders also need one-click unsubscribe.',
      'Send from a separate, authenticated domain rather than your main one, ramp up slowly, write each email to one person and stop as soon as they reply or opt out.',
    ],
    sections: [
      {
        id: 'hard-limits',
        title: 'The hard limit is not the real limit',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Google Workspace accounts can send up to 2,000 messages a day, and free Gmail accounts around 500. Those limits exist to stop abuse, not to tell you what is safe. A mailbox that writes to new contacts is judged on how they react long before it reaches any limit.',
          },
        ],
      },
      {
        id: 'google-rules',
        title: 'What Google checks',
        level: 2,
        blocks: [
          {
            type: 'list',
            items: [
              'Authentication: every sender needs SPF or DKIM on their domain; bulk senders need SPF, DKIM and DMARC. Check that all three are set for the domain you send from.',
              'Reported spam: Google asks senders to keep the spam rate reported in Postmaster Tools below 0.3%, and recommends staying under 0.1%.',
              'Unsubscribe: senders of more than 5,000 messages a day to Gmail must support one-click unsubscribe. Below that, a clear way to opt out still matters, and every opt-out has to be honored.',
            ],
          },
          {
            type: 'source',
            label: 'Google: Email sender guidelines',
            href: 'https://support.google.com/a/answer/81126',
            description:
              'Google’s requirements for everyone who sends to Gmail.',
          },
        ],
      },
      {
        id: 'separate-domain',
        title: 'Keep your main domain out of it',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Your company domain carries your invoices, support and everyday mail. If outreach hurts its reputation, all of that suffers. That is why outreach usually goes out from a separate domain with its own authenticated mailboxes, warmed up before the first real email, while replies still reach the person who answers them.',
          },
          {
            type: 'callout',
            title: 'How Laplace x GTM sends',
            body: 'Emails go out from pre-warmed mailboxes on domains assigned to your account, never from your own domain, and replies land in your Laplace inbox. Nothing is sent until you press Send.',
          },
        ],
      },
      {
        id: 'safe-pace',
        title: 'A safe daily pace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'There is no official number, so treat the following as a cautious practice rather than a rule. It is the pace Laplace x GTM uses for each sending mailbox.',
          },
          {
            type: 'table',
            columns: ['Period', 'Emails per day, per mailbox'],
            rows: [
              ['First week', 'up to 12'],
              ['Second week', 'up to 20'],
              ['After that', 'up to 25'],
            ],
          },
          {
            type: 'list',
            items: [
              'Spread sends across the working day instead of sending in one burst.',
              'Send only to verified addresses: keep bounces under 2%.',
              'Count follow-ups and answers in the total, and stop following up as soon as someone answers.',
            ],
          },
        ],
      },
      {
        id: 'what-gets-replies',
        title: 'What keeps you in the inbox',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Mailbox providers learn from recipients. Emails that people answer, or at least do not mark as spam, earn the next ones a place in the inbox. That favors short, plain-text emails written to one person about one problem they plausibly have, with one clear question at the end, and a quick, respectful stop when someone says no.',
          },
        ],
      },
    ],
  },
  {
    language: 'en',
    type: 'guide',
    slug: 'who-to-sell-to-first',
    title: 'Who to Sell to First: Ranking Buyer Teams by Budget Share',
    description:
      'Split your market by the team that buys, rate each campaign on five criteria and turn the ratings into budget shares, so your first emails go where they are most likely to land.',
    author: 'Laplace Team',
    publishedAt: '2026-10-04',
    readingTime: '6 min read',
    coverImage: '/images/who-to-sell-to-first-news.png',
    coverImageAlt:
      'Campaigns ranked by budget share, the top one aimed at a target',
    topics: ['Go-to-market', 'ICP', 'Sales'],
    published: true,
    seoTitle: 'Who to Sell to First: Rank Buyer Teams | Laplace',
    seoDescription:
      'A simple method to decide which buyer team to target first: campaigns by buying team, five ratings, and budget shares that concentrate on the best bet.',
    relatedResources: ['safe-outreach-email-from-gmail'],
    ctaType: 'find-customers',
    keyTakeaways: [
      'Split campaigns by the team that feels the problem and buys (support, sales, engineering, founders), not by industry.',
      'Rate each campaign from 1 to 5 on pain, proof, fit, reachability and displacement, then combine the ratings with fixed weights.',
      'Give budget in proportion to the square of each score: it concentrates on the best campaign without starving the rest.',
    ],
    sections: [
      {
        id: 'campaigns-by-team',
        title: 'Split by the team that buys',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A list such as “fintech, healthcare, VP Sales, competitors of X” mixes four different things. Prospects do not experience your product by industry; a support lead and a CFO at the same company care about different problems and answer different emails.',
          },
          {
            type: 'paragraph',
            text: 'So make each campaign one buyer team, named the way the buyer’s org chart names it: “Support teams”, “Sales teams”, “Founders”. Industries, company sizes and the competitor they likely use become details inside the campaign.',
          },
        ],
      },
      {
        id: 'five-ratings',
        title: 'Rate each campaign on five criteria',
        level: 2,
        blocks: [
          {
            type: 'table',
            columns: ['Criterion', 'Question'],
            rows: [
              ['Pain', 'How much does this team feel the problem you solve?'],
              [
                'Proof',
                'Can you point to a customer or result this team will believe?',
              ],
              [
                'Fit',
                'Does your product work for them today, without custom work?',
              ],
              ['Reachability', 'Can you find these people and their emails?'],
              [
                'Displacement',
                'If they use a competitor, how easy is it to switch?',
              ],
            ],
          },
          {
            type: 'paragraph',
            text: 'Rate each from 1 to 5 and combine them with fixed weights into a score from 0 to 100. Fixed weights matter: they keep the comparison honest when you are tempted to favor the campaign you like.',
          },
        ],
      },
      {
        id: 'budget-shares',
        title: 'Turn scores into budget shares',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Split the budget in proportion to the square of each score, then round the shares to whole percents that add up to 100. Squaring rewards the leader: a campaign that scores a bit higher gets clearly more, while weaker ones still get enough emails to prove you wrong.',
          },
          {
            type: 'table',
            columns: ['Campaign (score)', 'Budget share'],
            rows: [
              ['Sales teams (80)', '38%'],
              ['Founders (64)', '24%'],
              ['Operations (55)', '18%'],
              ['Support teams (45)', '12%'],
              ['Finance (37)', '8%'],
            ],
          },
          {
            type: 'paragraph',
            text: 'With $50 at $0.05 per email, that is about 380 emails to sales teams and 80 to finance. Revisit the shares after the first replies: real answers beat any rating.',
          },
        ],
      },
    ],
  },
]
